import type { AxiosError } from 'axios'
import axios from 'axios'
import { axiosInstance } from '@/services/apiClient'
import type { AuthStore } from '../stores/authStore'
import type { User } from '../types/authTypes'

interface AuthResponse {
  accessToken: string
  refreshToken: string
}

export const authService = {
  async login(authStore: AuthStore, email: string, password: string): Promise<User | undefined> {
    authStore.setLoading(true)
    try {
      const response = await axiosInstance.post<AuthResponse>('/auth/login', { email, password })
      const { accessToken, refreshToken } = response.data
      authStore.setAuth(accessToken, refreshToken)

      return authStore.user || undefined
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>
      authStore.setError(axiosError.response?.data?.message || 'Login failed')
      return undefined
    } finally {
      authStore.setLoading(false)
    }
  },

  async register(
    authStore: AuthStore,
    payload: {
      username: string
      email: string
      password: string
      genderIdentity?: string | null
      pronouns?: string | null
    }
  ): Promise<User | undefined> {
    authStore.setLoading(true)
    try {
      const response = await axiosInstance.post<AuthResponse>('/auth/register', payload)
      const { accessToken, refreshToken } = response.data
      authStore.setAuth(accessToken, refreshToken)

      return authStore.user || undefined
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>
      authStore.setError(axiosError.response?.data?.message || 'Registration failed')
      return undefined
    } finally {
      authStore.setLoading(false)
    }
  },

  async logout(authStore: AuthStore): Promise<void> {
    authStore.setLoading(true)

    try {
      await axios.post(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/logout`,
        {},
        {
          headers: authStore.token
            ? { Authorization: `Bearer ${authStore.token}` }
            : undefined,
        }
      )
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>
      authStore.setError(
        axiosError.response?.data?.message || 'Logout request failed, but local session was cleared.'
      )
    } finally {
      authStore.clearAuth()
      authStore.setLoading(false)
      window.location.assign('/login')
    }
  },

  async refreshToken(authStore: AuthStore): Promise<string> {
    authStore.setLoading(true)
    try {
      const refreshToken = authStore.refreshToken
      if (!refreshToken) throw new Error('Missing refresh token')

      const response = await axiosInstance.post<{ accessToken: string }>('/auth/refresh', { refreshToken })

      const { accessToken } = response.data
      authStore.token = accessToken
      localStorage.setItem('auth_token', accessToken)
      return accessToken
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>
      authStore.setError(axiosError.response?.data?.message || 'Token refresh failed')
      throw error
    } finally {
      authStore.setLoading(false)
    }
  },

  async forgotPassword(authStore: AuthStore, email: string): Promise<void> {
    authStore.setLoading(true)
    try {
      await axiosInstance.post('/auth/forgot-password', { email })
      authStore.clearError() // clear any old errors on success
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>
      authStore.setError(axiosError.response?.data?.message || 'Password reset failed')
      throw error
    } finally {
      authStore.setLoading(false)
    }
  },
}
